import React, { useState, useEffect, useMemo, useRef } from 'react'
import { WORK_DAYS, avatarStyle, initials } from '../pages/Dashboard'
import { TODAY, addDays, dk, isToday, fmtDate, fmtDow, isOutOnDate, fmtPhone, fmtLastWorked, fmtDowLong, etToday, spanDays, fmtSpanLabel, fmtWeekendPolicy } from '../lib/dateUtils'
import { publishSchedule, loadCostCodesForJob, emailSchedule, dispatchTexts, loadTextStatus, loadOptIns, setOptIn, sendOptinConfirmations } from '../lib/db'
import { buildScheduleEmailText, summarizePublishAndEmail } from '../lib/scheduleEmail'
import { isSchedulable } from '../lib/roster'
import DispatchSendModal from './DispatchSendModal'
import SearchCombo from './SearchCombo'

// A 'pending' recipient whose text Twilio reported undelivered/failed never GOT the ask.
// The confirmations board shows them apart from "no reply yet" (delivery_status comes from
// /api/text/status via the Twilio status callback; NULL = no verdict yet = ordinary pending).
const isUndeliveredRow = (w) => w && (w.delivery_status === 'undelivered' || w.delivery_status === 'failed')

// Lives in its own file (index.js is already 1300+ lines) but is re-exported here so
// callers can keep importing components from one place, like Board.js / CellModal.js.
export { default as DispatchSendModal } from './DispatchSendModal'
export { default as CertsView } from './CertsView'


// Board dateKey is toDateString() ("Tue May 06 2026"); the text endpoints want YYYY-MM-DD (local).
function ymdFromDateKey(dateKey) {
  const d = new Date(dateKey)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
const firstNameOf = (n) => String(n || '').trim().split(/\s+/)[0] || String(n || '')

// Desktop crew-dispatch strip for one job/day on Who's Where: send the dispatch
// texts (each worker gets an individual Y/N request) and see the live confirmation
// board. Reuses the same dispatchTexts / loadTextStatus backend as the mobile app.
function DispatchTextsDesktop({ job, dateKey, workerCount, canSend = true }) {
  const [sending, setSending] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [board, setBoard] = useState(null)
  const [note, setNote] = useState('')
  const dateYmd = ymdFromDateKey(dateKey)

  const refresh = async () => {
    setLoadingStatus(true); setNote('')
    const r = await loadTextStatus(dateYmd, job.id)
    setLoadingStatus(false)
    if (r.error) { setNote(r.error === 'not_signed_in' ? 'Sign in to view confirmations.' : `Status unavailable: ${r.error}`); return }
    const row = (r.jobs || []).find(x => String(x.job_id) === String(job.id))
    setBoard(row || { counts: { confirmed: 0, declined: 0, pending: 0, total: 0 }, confirmed: [], declined: [], pending: [] })
  }

  const send = async () => {
    if (!window.confirm(`Send dispatch texts to the ${workerCount} crew on ${job.num} – ${job.name}?\n\nEach worker gets an individual text asking them to reply Y or N.`)) return
    setSending(true); setNote('')
    const r = await dispatchTexts(dateYmd, job.id)
    setSending(false)
    if (r.error) { setNote(`Send failed: ${r.error}${r.detail ? ' — ' + r.detail : ''}`); return }
    setNote(`Sent ${r.texts_sent ?? 0} text${r.texts_sent === 1 ? '' : 's'}${r.texts_failed ? `, ${r.texts_failed} failed` : ''}.${(r.skipped_no_optin && r.skipped_no_optin.length) ? ` — ⚠ ${r.skipped_no_optin.length} NOT texted (no opt-in yet): ${r.skipped_no_optin.join(', ')}` : ''}`)
    refresh()
  }

  const chip = (label, n, bg, fg) => (
    <span style={{ background: bg, color: fg, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{label} {n}</span>
  )

  return (
    <div style={{ background: '#FAFAFA', borderLeft: `3px solid ${job.color}`, padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {canSend && <button className="btn btn-sm" disabled={sending} onClick={send}>{sending ? 'Sending…' : `📨 Send dispatch texts (${workerCount})`}</button>}
        <button className="btn btn-sm" disabled={loadingStatus} onClick={refresh}>{loadingStatus ? '…' : '↻ Confirmations'}</button>
        {note && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{note}</span>}
      </div>
      {board && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: 'var(--text)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {chip('✅', board.confirmed.length, '#EAF7EE', '#1E7E34')}
            {chip('❌', board.declined.length, '#FDECEC', '#A12622')}
            {chip('⏳', board.pending.filter(w => !isUndeliveredRow(w)).length, '#EDEDED', '#666')}
            {board.pending.some(isUndeliveredRow) && chip('📵', board.pending.filter(isUndeliveredRow).length, '#FFF1E6', '#B4451A')}
          </div>
          {board.confirmed.length > 0 && <span><b>Coming:</b> {board.confirmed.map(w => firstNameOf(w.name)).join(', ')}</span>}
          {board.declined.length > 0 && <span><b>Out:</b> {board.declined.map(w => firstNameOf(w.name)).join(', ')}</span>}
          {board.pending.some(w => !isUndeliveredRow(w)) && <span style={{ color: 'var(--text3)' }}><b>No reply:</b> {board.pending.filter(w => !isUndeliveredRow(w)).map(w => firstNameOf(w.name)).join(', ')}</span>}
          {board.pending.some(isUndeliveredRow) && <span style={{ color: '#B4451A' }}><b>Not delivered:</b> {board.pending.filter(isUndeliveredRow).map(w => `${firstNameOf(w.name)}${w.delivery_error_text ? ` (${w.delivery_error_text})` : ''}`).join(', ')}</span>}
        </div>
      )}
    </div>
  )
}

// ── Who's Where ──────────────────────────────────────────────────────────────
export function WhosWhere({ cellData, roster, activeJobs, onMarkOut, onExportPDF, role, currentUserEmail, userId, onOpenCell }) {
  const [dateKey, setDateKey] = useState(dk(addDays(TODAY, 1)))
  const [publishing, setPublishing] = useState(false)
  const [sendModal, setSendModal] = useState(null)   // { ymd, dayLabel } — the dispatch + no-work modal
  const [toast, setToast] = useState(null) // { kind: 'success'|'error', msg }

  const dateObj = new Date(dateKey)
  const assigned = {}
  activeJobs.forEach(j => {
    const c = cellData[j.id]?.[dateKey]
    if (c?.count > 0) {
      c.workers.forEach(w => { assigned[w] = { job: j, task: c.task, isForeman: false } })
      assigned[c.foreman] = { job: j, task: c.task, isForeman: true }
    }
  })
  // The headline strip counts FIELD CREW, so it is computed off the schedulable roster —
  // otherwise "unassigned" silently includes every PM and owner, who are never assignable.
  // `sorted` stays the FULL roster: the assigned/out sections below must still render an
  // office person who actually worked that day.
  const schedulableRoster = roster.filter(isSchedulable)
  const totalOut = schedulableRoster.filter(e => isOutOnDate(e, dateObj)).length
  const totalAssigned = Object.keys(assigned).filter(n => { const e = schedulableRoster.find(r => r.name === n); return e && !isOutOnDate(e, dateObj) }).length
  const sorted = [...roster].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="card">
      <div className="panel-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="panel-title">Who's where —</span>
          <input
              type="date"
              value={new Date(dateKey).toLocaleDateString('en-CA')}
              onChange={e => {
                if (!e.target.value) return
                const [y, m, d] = e.target.value.split('-').map(Number)
                setDateKey(new Date(y, m - 1, d, 12, 0, 0).toDateString())
              }}
              style={{ borderRadius: 8, border: '0.5px solid var(--border2)', padding: '6px 10px', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }}
            />
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{totalAssigned} assigned · {schedulableRoster.length - totalAssigned - totalOut} unassigned · {totalOut} out</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(role === 'owner' || role === 'road_super') && (
            <button className="btn btn-sm" disabled={publishing} onClick={async () => {
              const parsed = new Date(dateKey)
              const dateStr = parsed.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
              // Board dateKey is toDateString() format ("Tue May 06 2026") — convert to YYYY-MM-DD (local).
              const ymd = parsed.getFullYear() + '-' +
                String(parsed.getMonth() + 1).padStart(2, '0') + '-' +
                String(parsed.getDate()).padStart(2, '0')
              if (!window.confirm(`Publish the schedule for ${dateStr}?\n\nThis creates/updates the daily field forms and emails each foreman (cc road supers), then emails the full schedule to the office team from the Crew Dispatch address.`)) return
              // Runs AFTER the publish API succeeds: build the shared plain-text
              // schedule body (src/lib/scheduleEmail.js — the SAME builder the
              // mobile Publish button uses) and auto-send it from the Crew
              // Dispatch address via /api/schedule/email-schedule (recipients
              // resolved server-side: all non-foreman app users, publisher
              // included). Replaced the old clipboard + mailto flow 2026-07-23.
              const sendScheduleEmail = async () => {
                const { subject, body } = buildScheduleEmailText({ dateKey, activeJobs, cellData, roster })
                return await emailSchedule(subject, body)
              }

              setToast(null)
              setPublishing(true)
              try {
                const r = await publishSchedule(ymd)
                if (r.error) {
                  setToast({ kind: 'error', msg: `Publish failed: ${r.error}${r.detail ? ' — ' + r.detail : ''}` })
                  return
                }
                // Publish succeeded — auto-send the schedule email, then toast
                // once via the shared summary (same message the mobile alert shows).
                const se = await sendScheduleEmail()
                const { msg, isError } = summarizePublishAndEmail(r, se, dateStr)
                setToast({ kind: isError ? 'error' : 'success', msg })
              } catch (e) {
                setToast({ kind: 'error', msg: `Publish failed: ${e.message}` })
              } finally {
                setPublishing(false)
              }
            }}>{publishing ? 'Publishing…' : 'Publish schedule'}</button>
          )}
          {(role === 'owner' || role === 'road_super') && (
            // Opens DispatchSendModal, which does BOTH jobs in one pass: the dispatch
            // preview (formerly crammed into a window.confirm) plus the "no work
            // tomorrow" picker for crew who are NOT on the board that day. The modal
            // owns the preview → confirm → send flow; the result lands in this toast.
            // Mobile keeps its own window.confirm dispatch flow — see the note in
            // DispatchSendModal.js.
            <button className="btn btn-sm" disabled={!!sendModal} onClick={() => {
              setToast(null)
              setSendModal({
                ymd: ymdFromDateKey(dateKey),
                dayLabel: new Date(dateKey).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
              })
            }}>📨 Send all dispatch texts</button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => onExportPDF(dateKey)}>Export PDF</button>
        </div>
      </div>
      {sendModal && (
        <DispatchSendModal
          dateYmd={sendModal.ymd}
          dayLabel={sendModal.dayLabel}
          onDone={(t) => setToast(t)}
          onClose={() => setSendModal(null)}
        />
      )}
      {toast && (
        <div style={{
          margin: '0 0 12px', padding: '10px 14px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          background: toast.kind === 'success' ? 'var(--green-bg, #EAF7EE)' : 'var(--red-bg, #FDECEC)',
          color: toast.kind === 'success' ? 'var(--green-text, #1E7E34)' : 'var(--red-text, #A12622)',
          border: `1px solid ${toast.kind === 'success' ? '#bfe3c8' : '#f3c2c2'}`,
        }}>
          <span>{toast.kind === 'success' ? '✅ ' : '⚠️ '}{toast.msg}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}
      {/* Jobs with assigned crew */}
      {(() => {
        // Build duplicate worker map
        const workerJobCount = {}
        activeJobs.forEach(j => {
          const c = cellData[j.id]?.[dateKey]
          if (!c || c.count === 0) return
          const workers = c.foreman ? [c.foreman, ...(c.workers || []).filter(w => w !== c.foreman)] : (c.workers || [])
          workers.forEach(w => { workerJobCount[w] = (workerJobCount[w] || 0) + 1 })
        })
        const isDuplicate = (name) => workerJobCount[name] > 1
        const canEdit = role === 'owner' || role === 'road_super'
        return activeJobs.map(j => {
        const c = cellData[j.id]?.[dateKey]
        if (!c || c.count === 0) return null
        const jobWorkers = [...(c.workers || [])]
        // Put foreman first if not already included
        const allWorkers = c.foreman && !jobWorkers.includes(c.foreman)
          ? [c.foreman, ...jobWorkers]
          : c.foreman
            ? [c.foreman, ...jobWorkers.filter(w => w !== c.foreman)]
            : jobWorkers
        return (
          <div key={j.id} style={{ marginBottom: 16 }}>
            {/* Job header */}
            <div style={{ background: j.color, borderRadius: '8px 8px 0 0', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, cursor: canEdit ? 'pointer' : 'default' }}
                onClick={() => canEdit && onOpenCell && onOpenCell({ jobId: j.id, dateKey })}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {j.num} – {j.name}
                  {canEdit && <span style={{ fontSize: 10, opacity: 0.75 }}>✏️</span>}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>
                  {j.address && <span>{j.address}</span>}
                  {(j.pmName || j.pm) && <span style={{ marginLeft: 8 }}>PM: {j.pmName || j.pm}</span>}
                  {j.gc && <span style={{ marginLeft: 8 }}>GC: {j.gc}</span>}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 700, color: '#fff' }}>{c.count} workers</div>
            </div>
            {/* Foreman / Task bar */}
            <div style={{ background: '#F5F5F5', padding: '6px 14px', borderLeft: `3px solid ${j.color}`, display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
              {c.foreman && <span><span style={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', fontSize: 10, letterSpacing: '.4px' }}>Foreman </span><span style={{ fontWeight: 600 }}>{c.foreman}</span></span>}
              {c.task && <span><span style={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', fontSize: 10, letterSpacing: '.4px' }}>Task </span><span>{c.task}</span></span>}
            </div>
            {/* Crew dispatch: owner/road super get send + confirmations; everyone else confirmations only */}
            <DispatchTextsDesktop job={j} dateKey={dateKey} workerCount={c.count} canSend={canEdit} />
            {/* Workers */}
            <div style={{ border: '0.5px solid var(--border)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              {allWorkers.map((wname, wi) => {
                const emp = roster.find(e => e.name === wname) || { trade: '', local: '', phone: '', cellPhone: '' }
                const phone = fmtPhone(emp.cellPhone || emp.phone || '')
                const isForeman = wname === c.foreman
                return (
                  <div key={wi} style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: wi < allWorkers.length - 1 ? '0.5px solid var(--border)' : 'none', background: isDuplicate(wname) ? '#FFF8E1' : 'var(--surface)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: isForeman ? 600 : 400, color: isDuplicate(wname) ? '#B45309' : 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {wname}
                        {isForeman && <span style={{ fontSize: 9, background: 'var(--blue-bg)', color: 'var(--blue-text)', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>FOREMAN</span>}
                        {isDuplicate(wname) && <span style={{ fontSize: 9, background: '#FEF3C7', color: '#B45309', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>⚠ DOUBLE BOOKED</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                        {emp.local || ''}
                        {phone && <span style={{ marginLeft: 8 }}>{phone}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })
      })()}

      {/* Unavailable */}
      {(() => {
        const outWorkers = sorted.filter(e => isOutOnDate(e, dateObj))
        if (!outWorkers.length) return null
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ background: '#A32D2D', borderRadius: '8px 8px 0 0', padding: '8px 14px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Unavailable — {outWorkers.length}</div>
            </div>
            <div style={{ border: '0.5px solid #F09595', borderTop: 'none', borderRadius: '0 0 8px 8px', background: '#FFF5F5', overflow: 'hidden' }}>
              {outWorkers.map((emp, wi) => (
                <div key={emp.id} style={{ padding: '7px 14px', borderBottom: wi < outWorkers.length - 1 ? '0.5px solid #F09595' : 'none', fontSize: 13, color: '#A32D2D', fontWeight: 500 }}>
                  {emp.name}
                  <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>{emp.local || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Unassigned */}
      {(() => {
        const assignedNames = new Set(Object.keys(assigned))
        const unassigned = sorted
          .filter(e => isSchedulable(e) && !assignedNames.has(e.name) && !isOutOnDate(e, dateObj))
          .sort((a, b) => {
            // Sort by date last worked descending (most recent first)
            if (!a.dateLastWorked && !b.dateLastWorked) return 0
            if (!a.dateLastWorked) return 1
            if (!b.dateLastWorked) return -1
            return b.dateLastWorked.localeCompare(a.dateLastWorked)
          })
        if (!unassigned.length) return null
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ background: '#666', borderRadius: '8px 8px 0 0', padding: '8px 14px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Unassigned — {unassigned.length}</div>
            </div>
            <div style={{ border: '0.5px solid var(--border)', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              {unassigned.map((emp, wi) => {
                const phone = fmtPhone(emp.cellPhone || emp.phone || '')
                const lastWorked = fmtLastWorked(emp.dateLastWorked)
                return (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: wi < unassigned.length - 1 ? '0.5px solid var(--border)' : 'none', background: 'var(--surface)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>{emp.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                        {emp.local || ''}
                        {phone && <span style={{ marginLeft: 8 }}>{phone}</span>}
                        {lastWorked && <span style={{ marginLeft: 8, color: 'var(--text3)' }}>Last worked: {lastWorked}</span>}
                      </div>
                    </div>
                    <button className="btn btn-sm" style={{ fontSize: 10, marginLeft: 8 }} onClick={() => onMarkOut(emp.id)}>
                      Mark out
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ── Roster Panel ─────────────────────────────────────────────────────────────
export function RosterPanel({ roster, onOpenTimeOff }) {
  const [query, setQuery] = useState('')
  const [showUpcoming, setShowUpcoming] = useState(false)
  const [showOptIns, setShowOptIns] = useState(false)
  const q = query.trim().toLowerCase()
  const matches = (e) => !q || [e.name, e.local, e.cellPhone, e.phone]
    .some(v => String(v || '').toLowerCase().includes(q))
  // Sort: ACTIVE on top / INACTIVE at the bottom, foremen before workers, then A–Z.
  const filtered = [...roster].filter(matches).sort((a, b) => {
    const aAct = a.active !== false, bAct = b.active !== false
    if (aAct !== bAct) return aAct ? -1 : 1
    if (a.isForeman !== b.isForeman) return a.isForeman ? -1 : 1
    return a.name.localeCompare(b.name)
  })
  const activeCount = roster.filter(e => e.active !== false).length
  const inactiveCount = roster.length - activeCount
  // Group divider: Foremen / Workers for active, a single Inactive group at the end.
  const groupLabel = (e) => e.active === false ? 'Inactive' : (e.isForeman ? 'Foremen' : 'Workers')
  let lastGroup = null

  return (
    <div className="card">
      <div className="panel-header">
        <span className="panel-title">Crew roster <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 12 }}>{q ? `${filtered.length} shown` : `${activeCount} active · ${inactiveCount} inactive`}</span></span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={() => setShowOptIns(true)}>📱 Text opt-ins</button>
          <button className="btn btn-sm" onClick={() => setShowUpcoming(true)}>📅 Upcoming time off</button>
          <button className="btn btn-primary btn-sm" disabled={!filtered.length} onClick={() => filtered[0] && onOpenTimeOff(filtered[0].id)}>+ Mark time off</button>
        </div>
      </div>
      <div style={{ padding: '8px 12px', borderBottom: '0.5px solid var(--border)' }}>
        <input className="form-input" type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by name, local, or phone…" style={{ width: '100%' }} />
      </div>
      {filtered.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No employees match &ldquo;{query.trim()}&rdquo;.</div>
      )}
      {filtered.map((emp, i) => {
        const isInactive = emp.active === false
        const isOut = isOutOnDate(emp, TODAY)
        const sections = []
        const grp = groupLabel(emp)
        if (lastGroup !== grp) {
          sections.push(<div key={'lbl-' + grp} className="section-lbl">{grp}</div>)
          lastGroup = grp
        }
        const toStr = emp.timeOff.map(t => `${t.from} – ${t.to}${t.reason ? ' (' + t.reason + ')' : ''}`).join(', ')
        sections.push(
          <div key={emp.id} className="emp-row" style={isInactive ? { opacity: 0.5 } : undefined}>
            <div className="avatar" style={avatarStyle(i)}>{initials(emp.name)}</div>
            <div className="emp-info">
              <div className="emp-name">{emp.name}</div>
              <div className="emp-meta">{emp.local}</div>
              {(emp.cellPhone || emp.phone) && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                  {emp.cellPhone ? <span>📱 {emp.cellPhone}</span> : null}
                  {emp.cellPhone && emp.phone ? <span style={{ margin: '0 6px' }}>·</span> : null}
                  {emp.phone && !emp.cellPhone ? <span>☎ {emp.phone}</span> : null}
                </div>
              )}
              {toStr && <div className="emp-out-dates">Out: {toStr}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isInactive
                ? <span className="pill" style={{ background: 'var(--surface2)', color: 'var(--text3)', border: '0.5px solid var(--border)' }}>Inactive</span>
                : <span className={`pill ${isOut ? 'out-badge' : 'avail-badge'}`}>{isOut ? 'Out today' : 'Available'}</span>}
              <button className="btn btn-sm" onClick={() => onOpenTimeOff(emp.id)}>Time off</button>
            </div>
          </div>
        )
        return sections
      })}
      {showUpcoming && <UpcomingTimeOffModal roster={roster} onClose={() => setShowUpcoming(false)} />}
      {showOptIns && <TextOptInsModal onClose={() => setShowOptIns(false)} />}
    </div>
  )
}

// On-demand view of ALL upcoming employee time off (end date today-or-later,
// in America/New_York), soonest start first. Mirrors the weekly digest email.
// Crew texting consent board. Shows every active employee's SMS opt-in state
// (opted in / opted out / never signed up) and — for owner/road_super — lets the
// office record a PAPER consent form or revoke consent. Only opted-in phones are
// texted by api/text/dispatch.js, so this is the list that governs who gets
// dispatch texts. Permission comes back on the GET (can_edit), so no role prop.
function TextOptInsModal({ onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(null)   // phone_last10 currently being written
  const [confirming, setConfirming] = useState(false)

  const refresh = async () => {
    setLoading(true); setErr('')
    const res = await loadOptIns()
    if (res.error) setErr(res.detail || res.error)
    else setData(res)
    setLoading(false)
  }
  useEffect(() => { refresh() }, [])

  const act = async (person, action) => {
    const verb = action === 'record' ? 'Record a signed PAPER consent form for' : 'Revoke text consent for'
    if (!window.confirm(`${verb} ${person.name} (${fmtPhone(person.phone)})?`)) return
    setBusy(person.phone_last10)
    const res = await setOptIn(action, { name: person.name, phone: person.phone })
    setBusy(null)
    if (res.error) { window.alert(`Could not save: ${res.detail || res.error}`); return }
    refresh()
  }

  const sendConfirmations = async () => {
    const n = c ? c.needs_confirmation : 0
    if (!window.confirm(`Send the confirmation text to ${n} ${n === 1 ? 'person' : 'people'} who never got one?\n\nThey'll be asked to reply Y to confirm. Any number that fails is a bad number.`)) return
    setConfirming(true)
    const res = await sendOptinConfirmations()
    setConfirming(false)
    if (res.error) { window.alert(`Could not send: ${res.detail || res.error}`); return }
    const bad = (res.failed || []).map(f => f.name || f.phone).join(', ')
    window.alert(`Sent ${res.sent} of ${res.candidates}.` + (bad ? `\n\n⚠ Could not reach: ${bad}\nThese are bad numbers — correct them and re-send.` : ''))
    refresh()
  }

  const c = data ? data.counts : null
  const canEdit = !!(data && data.can_edit)
  const q = query.trim().toLowerCase()
  const people = (data ? data.people : []).filter(p => {
    if (filter === 'no_phone' ? !!p.phone_last10 : (filter !== 'all' && p.status !== filter)) return false
    return !q || String(p.name || '').toLowerCase().includes(q)
  })

  const badge = (status) => {
    const map = {
      opted_in:  { t: 'Opted in',      bg: 'rgba(52,199,89,0.15)',  fg: '#248a3d' },
      opted_out: { t: 'Opted out',     bg: 'rgba(255,59,48,0.15)',  fg: '#c9302c' },
      none:      { t: 'Not signed up', bg: 'rgba(142,142,147,0.18)', fg: 'var(--text3)' },
    }
    const m = map[status] || map.none
    return <span style={{ background: m.bg, color: m.fg, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>{m.t}</span>
  }

  const tab = (key, label, n) => (
    <button key={key} className={'btn btn-sm' + (filter === key ? ' btn-primary' : '')}
      onClick={() => setFilter(key)}>{label}{n == null ? '' : ` (${n})`}</button>
  )

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, width: '92%' }}>
        <div className="modal-title">Crew text opt-ins</div>
        <div className="modal-sub">
          Only opted-in numbers receive dispatch texts.
          {c ? ` ${c.opted_in} of ${c.total} active employees are opted in.` : ''}
        </div>

        {err && <div style={{ color: '#c9302c', fontSize: 12, padding: '6px 0' }}>{err}</div>}
        {loading && !data && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}

        {data && (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '4px 0 8px' }}>
              {tab('all', 'All', c.total)}
              {tab('opted_in', 'Opted in', c.opted_in)}
              {tab('none', 'Not signed up', c.none)}
              {tab('opted_out', 'Opted out', c.opted_out)}
              {c.no_phone > 0 && tab('no_phone', 'No phone', c.no_phone)}
            </div>
            <input className="form-input" type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search by name…" style={{ width: '100%', marginBottom: 8 }} />

            <div style={{ maxHeight: 380, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
              {people.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Nobody in this group.</div>
              )}
              {people.map(p => (
                <div key={p.employee_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '0.5px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {p.name}
                      {p.is_foreman ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · foreman</span> : null}
                      {!p.active ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {p.include_reason === 'pm_on_active_job' ? 'PM, not on roster' : 'not on roster'}</span> : null}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {p.phone ? fmtPhone(p.phone) : 'no phone on file'}
                      {p.status === 'opted_in' && p.source === 'office' ? ' · paper form' : ''}
                      {p.status === 'opted_in' && p.confirmed ? ' · confirmed' : ''}
                      {p.status === 'opted_in' && !p.confirmed && p.confirmation_sent ? ' · awaiting reply' : ''}
                      {p.status === 'opted_in' && !p.confirmation_sent ? ' · unverified' : ''}
                      {p.status === 'opted_in' && p.source === 'web' ? ' · web form' : ''}
                      {p.status === 'opted_in' && p.source === 'onboarding' ? ' · auto-enrolled at hire' : ''}
                      {p.recorded_by ? ` · by ${p.recorded_by}` : ''}
                    </div>
                  </div>
                  {badge(p.status)}
                  {canEdit && p.phone_last10 && (
                    p.status === 'opted_in'
                      ? <button className="btn btn-sm" disabled={busy === p.phone_last10}
                          onClick={() => act(p, 'revoke')}>{busy === p.phone_last10 ? '…' : 'Revoke'}</button>
                      : <button className="btn btn-sm btn-primary" disabled={busy === p.phone_last10}
                          onClick={() => act(p, 'record')}>{busy === p.phone_last10 ? '…' : 'Paper form'}</button>
                  )}
                </div>
              ))}
            </div>

            {canEdit && c.needs_confirmation > 0 && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,159,10,0.12)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1 }}>
                  <strong>{c.needs_confirmation}</strong> signed up but were never sent a confirmation text,
                  so their number is unverified.
                </span>
                <button className="btn btn-sm btn-primary" disabled={confirming} onClick={sendConfirmations}>
                  {confirming ? 'Sending…' : 'Send confirmations'}
                </button>
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text3)', paddingTop: 8 }}>
              Send anyone not signed up to <strong>cornerstonecrewdispatch.com/sms-optin.html</strong>.
              {canEdit ? ' Use “Paper form” only when a signed consent form is on file.' : ''}
            </div>
          </>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={refresh} disabled={loading}>Refresh</button>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  )
}

function UpcomingTimeOffModal({ roster, onClose }) {
  const today = etToday()
  const items = []
  for (const e of roster) {
    for (const t of (e.timeOff || [])) {
      if (t.to && t.to >= today) items.push({ name: e.name, from: t.from, to: t.to, reason: t.reason || '' })
    }
  }
  items.sort((a, b) => (a.from || '').localeCompare(b.from || '') || (a.to || '').localeCompare(b.to || '') || a.name.localeCompare(b.name))
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">Upcoming time off</div>
        <div className="modal-sub">All employees · soonest first{items.length ? ` · ${items.length}` : ''}</div>
        <div className="timeoff-list-box" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.length ? items.map((it, i) => (
            <div key={i} className="timeoff-row">
              <span>
                <strong>{it.name}</strong> - {fmtDowLong(it.from)}{it.to && it.to !== it.from ? ` to ${fmtDowLong(it.to)}` : ''}
                {it.reason ? <span style={{ color: 'var(--text3)' }}> · {it.reason}</span> : null}
              </span>
            </div>
          )) : <div style={{ padding: 10, fontSize: 12, color: 'var(--text3)' }}>No upcoming time off is currently on the books.</div>}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  )
}

// ── Requests ─────────────────────────────────────────────────────────────────
export function Requests({ requests, role, onApprove, onDeny, onDelete, currentUserEmail }) {
  if (!requests.length) return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
      No requests yet. PMs and foremen can submit requests using the + New request tab.
    </div>
  )
  const canAct = role === 'owner' || role === 'road_super'
  return (
    <div className="panel-pad">
      {['pending', 'approved', 'denied'].map(status => {
        const grp = requests.filter(r => r.status === status)
        if (!grp.length) return null
        return (
          <div key={status}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{status}</div>
            {grp.map(r => {
              const days = spanDays(new Date(r.dateKey), r.daysOnsite || 1,
                { includeSaturday: r.includeSaturday, includeSunday: r.includeSunday })
              return (
              <div key={r.id} className={`req-card ${r.status}`} style={{ marginBottom: 8 }}>
                <div className="req-header">
                  <div>
                    <div className="req-title">{r.jobName} — {fmtSpanLabel(days)}
                      {days.length > 1 && (
                        <span style={{ color: 'var(--text3)' }}> · {fmtWeekendPolicy(r.includeSaturday, r.includeSunday)}</span>
                      )}
                    </div>
                    <div className="req-meta">From: {r.requestedBy} ({r.role}) · {r.ts}</div>
                  </div>
                  <span className={`pill pill-${r.status}`}>{r.status}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 3 }}>
                  Requesting <strong>{r.workers}</strong> workers × <strong>{days.length}</strong> day(s) — <strong>{r.workers * days.length}</strong> man-days (currently on day 1: {r.current})
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>"{r.reason}"</div>
                {canAct && r.status === 'pending' && (
                  <div className="req-actions">
                    <button className="btn btn-success btn-sm" onClick={() => onApprove(r.id)}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => onDeny(r.id)}>Deny</button>
                  </div>
                )}
                {!canAct && r.status === 'pending' && role === 'pm' && typeof onDelete === 'function' && (
                  <div className="req-actions">
                    <button className="btn btn-sm" style={{ color: 'var(--red-text)', borderColor: 'var(--red-text)' }}
                      onClick={() => { if (window.confirm('Delete this request?')) onDelete(r.id) }}>
                      Delete request
                    </button>
                  </div>
                )}
              </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ── New Request ───────────────────────────────────────────────────────────────
export function NewRequest({ jobs, workDays, cellData, role, roster, onSubmit, onCancel, currentUserName, currentUserEmail }) {
  const [jobId, setJobId] = useState(jobs[0]?.id || '')
  const [dateKey, setDateKey] = useState(dk(addDays(TODAY, 1)))
  const [workers, setWorkers] = useState(5)
  const [daysOnsite, setDaysOnsite] = useState(1)
  const [includeSaturday, setIncludeSaturday] = useState(false)
  const [includeSunday, setIncludeSunday] = useState(false)
  const [reason, setReason] = useState('')
  // Double-click guard. The ref is the real lock — it updates synchronously, so a
  // second click dispatched in the SAME React batch sees it already taken. The
  // state flag only drives the disabled button (it would still read stale `false`
  // on that second click, which is how rows 8/9 landed 110ms apart).
  const [submitting, setSubmitting] = useState(false)
  const submitLock = useRef(false)

  const weekendOpts = { includeSaturday, includeSunday }
  const previewDays = spanDays(new Date(dateKey), daysOnsite, weekendOpts)
  // Does the chosen start day fall on a day the current setting excludes?
  const startDow = new Date(dateKey).getDay()
  const startExcluded = (startDow === 6 && !includeSaturday) || (startDow === 0 && !includeSunday)

  const handleSubmit = async () => {
    if (submitLock.current) return
    submitLock.current = true
    setSubmitting(true)
    try {
      const job = jobs.find(j => j.id === parseInt(jobId))
      const current = cellData[parseInt(jobId)]?.[dateKey]?.count || 0
      // Use the actual logged-in user's name, fall back to email
      const by = currentUserName || currentUserEmail || (role === 'pm' ? job.pm : job.foreman)
      const roleLabel = role === 'owner' ? 'Owner' : role === 'pm' ? 'PM' : 'Foreman'
      // The span start can shift off an excluded day, so every stored date comes from it.
      const days = spanDays(new Date(dateKey), daysOnsite, weekendOpts)
      const dateKeys = days.map(dk)
      await onSubmit({
        jobId: parseInt(jobId), jobName: job.num + ' ' + job.name, requestedBy: by, role: roleLabel,
        date: fmtDate(days[0]), dateKey: dateKeys[0],
        workers: parseInt(workers), current, reason: reason || 'No reason given',
        submitterEmail: currentUserEmail || '',
        daysOnsite: days.length, includeSaturday, includeSunday,
        endDateKey: dateKeys[dateKeys.length - 1], dateKeys,
      })
    } finally {
      // Runs on an early return or a throw too, so the lock can never stick.
      submitLock.current = false
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 420, padding: 4 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 16 }}>Submit manpower request</div>
      <div className="form-group"><label className="form-label">Job</label>
        <select className="form-select" value={jobId} onChange={e => setJobId(e.target.value)}>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.num} – {j.name}</option>)}
        </select>
      </div>
      <div className="form-group"><label className="form-label">Date needed</label>
        <select className="form-select" value={dateKey} onChange={e => setDateKey(e.target.value)}>
          {workDays.map(d => <option key={dk(d)} value={dk(d)}>{fmtDow(d)} {fmtDate(d)}{isToday(d) ? ' (today)' : ''}</option>)}
        </select>
      </div>
      <div className="form-group"><label className="form-label">Days onsite</label>
        <input className="form-input" type="number" value={daysOnsite} min={1} max={30}
          onChange={e => setDaysOnsite(e.target.value)} style={{ maxWidth: 100 }} />
      </div>
      <div className="form-group">
        <label className="form-label">Weekend work</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={includeSaturday} onChange={e => setIncludeSaturday(e.target.checked)} />
            Include Saturdays
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={includeSunday} onChange={e => setIncludeSunday(e.target.checked)} />
            Include Sundays
          </label>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Unchecked = Mon–Fri only</div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
        <strong>Covers:</strong> {fmtSpanLabel(previewDays)} · {fmtWeekendPolicy(includeSaturday, includeSunday)}
        {startExcluded && (
          <div style={{ marginTop: 2 }}>Start date is excluded by your weekend setting — the span begins on the next included day.</div>
        )}
      </div>
      <div className="form-group"><label className="form-label">Workers requested</label>
        <input className="form-input" type="number" value={workers} min={1} max={30} onChange={e => setWorkers(e.target.value)} style={{ maxWidth: 100 }} />
      </div>
      <div className="form-group"><label className="form-label">Reason / notes</label>
        <textarea className="form-textarea" value={reason} onChange={e => setReason(e.target.value)} placeholder="What's happening on site?" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}
          style={submitting ? { opacity: .6, cursor: 'not-allowed' } : undefined}>
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── Cell Modal ────────────────────────────────────────────────────────────────
export function CellModal({ job, dateKey, cell, roster, cellData = {}, allJobs = [], onSave, onClose }) {
  const [foreman, setForeman] = useState(cell.foreman || job.foreman || '')
  const [count, setCount] = useState(cell.count)
  const [task, setTask] = useState(cell.task || '')
  // Ensure foreman is always first in workers list on open
  const initWorkers = () => {
    const fm = cell.foreman || job.foreman || ''
    const existing = [...cell.workers]
    if (fm && !existing.includes(fm)) return [fm, ...existing]
    if (fm && existing.includes(fm)) return [fm, ...existing.filter(w => w !== fm)]
    return existing
  }
  const [workers, setWorkers] = useState(initWorkers)
  // Phase A: cost codes. Crew-wide default + per-worker overrides (keyed by name).
  const [defaultCostCode, setDefaultCostCode] = useState(cell.default_cost_code || '')
  const [workerCostCodes, setWorkerCostCodes] = useState(
    cell.worker_cost_codes && typeof cell.worker_cost_codes === 'object' ? { ...cell.worker_cost_codes } : {})
  const [jobCostCodes, setJobCostCodes] = useState([])
  const ccItems = useMemo(() => jobCostCodes.map(cc => ({ key: cc.code, value: cc.code, label: cc.code, sub: cc.description || '' })), [jobCostCodes])
  const setWorkerCC = (name, val) => setWorkerCostCodes(prev => {
    const next = { ...prev }
    if (val && val.trim()) next[name] = val.trim(); else delete next[name]
    return next
  })
  useEffect(() => {
    let on = true
    loadCostCodesForJob(job.id).then(list => { if (on) setJobCostCodes(list) })
    return () => { on = false }
  }, [job.id])
  const [search, setSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef(null)
  // Close the add-worker dropdown when clicking anywhere outside it
  useEffect(() => {
    const onDocDown = (ev) => {
      if (pickerRef.current && !pickerRef.current.contains(ev.target)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [])
  const [showCopy, setShowCopy] = useState(false)
  const [copyDays, setCopyDays] = useState([])
  const [copyDone, setCopyDone] = useState(false)
  const d = new Date(dateKey)
  const outCount = roster.filter(e => isOutOnDate(e, d)).length
  // Wrong-day guard: editing TODAY or a PAST day is almost always a mis-click for tomorrow.
  const isTodayCell = isToday(d)
  const isPastCell = d < TODAY && !isTodayCell
  const dayLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })

  const futureDays = WORK_DAYS.filter(wd => dk(wd) !== dateKey)

  const toggleCopyDay = (k) => setCopyDays(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])
  const selectAll = () => setCopyDays(futureDays.map(wd => dk(wd)))
  const clearAll = () => setCopyDays([])

  // Calendar grid (Sun–Sat week rows) covering all WORK_DAYS
  const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
  const workDaySet = new Set(WORK_DAYS.map(dk))
  const gridStart = addDays(WORK_DAYS[0], -WORK_DAYS[0].getDay())
  const lastWD = WORK_DAYS[WORK_DAYS.length - 1]
  const gridEnd = addDays(lastWD, 6 - lastWD.getDay())
  const totalGridDays = Math.round((gridEnd - gridStart) / 86400000) + 1
  const calendarWeeks = []
  for (let i = 0; i < totalGridDays; i += 7) {
    calendarWeeks.push(Array.from({ length: 7 }, (_, j) => addDays(gridStart, i + j)))
  }
  const hasPastSelected = copyDays.some(k => new Date(k) < TODAY)

  // Same-name twins (two active "Luis Tejada"s). The crew is stored as NAME strings, so
  // once one of them is on the crew the other is indistinguishable and un-addable until
  // the office sets a display name in Admin → Employees. Rather than hide the twin and
  // silently do nothing on click, keep him in the picker and say why he can't be added.
  const nameTwins = useMemo(() => {
    const counts = {}
    roster.forEach(e => { const k = String(e.name || '').trim().toLowerCase(); if (k) counts[k] = (counts[k] || 0) + 1 })
    return new Set(Object.keys(counts).filter(k => counts[k] > 1))
  }, [roster])
  const hasTwin = (e) => nameTwins.has(String(e.name || '').trim().toLowerCase())
  const [dupWarn, setDupWarn] = useState('')

  const addWorker = (emp) => {
    if (!workers.includes(emp.name)) {
      setWorkers(w => [...w, emp.name])
      setCount(c => c + 1)
      setSearch('')
      setDupWarn('')
    } else if (hasTwin(emp)) {
      const ids = roster.filter(e => String(e.name || '').trim().toLowerCase() === String(emp.name).trim().toLowerCase()).map(e => `#${e.id}`).join(', ')
      setDupWarn(`Two employees share the name "${emp.name}" (${ids}). Set a display name in Admin → Employees to schedule them separately.`)
    }
  }
  const removeWorker = (idx) => {
    setWorkers(w => w.filter((_, i) => i !== idx))
    setCount(c => Math.max(0, c - 1))
  }

  // Who's already working somewhere else this day? name -> [job labels]
  const assignedElsewhere = useMemo(() => {
    const map = {}
    allJobs.forEach(j => {
      if (j.id === job.id) return
      const ws = cellData[j.id]?.[dateKey]?.workers || []
      ws.forEach(name => {
        if (typeof name !== 'string' || name.startsWith('[')) return
        if (!map[name]) map[name] = []
        map[name].push(String(j.num || j.name || '').trim())
      })
    })
    return map
  }, [allJobs, cellData, dateKey, job.id])

  // "Recent" = actually part of the working crew: on the board somewhere in this
  // window, or last worked within RECENT_DAYS (Foundation sync). The employees
  // table carries ~200 old Foundation records still flagged active=true — those
  // never show when browsing and only surface on search, shaded red at the bottom.
  const RECENT_DAYS = 45
  const onBoardNames = useMemo(() => {
    const s = new Set()
    Object.values(cellData).forEach(days => Object.values(days || {}).forEach(c =>
      (c?.workers || []).forEach(n => { if (typeof n === 'string' && !n.startsWith('[')) s.add(n) })))
    return s
  }, [cellData])
  const isRecent = (e) => {
    if (onBoardNames.has(e.name)) return true
    if (!e.dateLastWorked) return false
    const parts = String(e.dateLastWorked).split('-')
    if (parts.length !== 3) return false
    const dlw = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
    return !isNaN(dlw) && (TODAY - dlw) / 86400000 <= RECENT_DAYS
  }

  // Dropdown contents: active roster, not already on this crew, filtered by search.
  // Grouped: available on top, assigned-elsewhere below (amber, still addable),
  // time off at the very bottom (disabled).
  const q = search.trim().toLowerCase()
  // A same-name twin stays listed even when "his" name is already on the crew, so the
  // click reaches addWorker's warning instead of the person simply not existing.
  const pickerMatches = roster.filter(e =>
    isSchedulable(e) &&
    (!workers.includes(e.name) || hasTwin(e)) &&
    (q.length === 0 ||
     e.name.toLowerCase().includes(q) ||
     String(e.id).includes(q) ||
     (e.local || '').toLowerCase().includes(q))
  )
  const byName = (a, b) => a.name.localeCompare(b.name)
  const availList = pickerMatches.filter(e => !isOutOnDate(e, d) && !assignedElsewhere[e.name] && isRecent(e)).sort(byName)
  const assignedList = pickerMatches.filter(e => !isOutOnDate(e, d) && assignedElsewhere[e.name]).sort(byName)
  const outList = pickerMatches.filter(e => isOutOnDate(e, d) && isRecent(e)).sort(byName)
  // Old Foundation records: search-only, shaded red at the very bottom
  const staleList = q.length > 0
    ? pickerMatches.filter(e => !assignedElsewhere[e.name] && !isRecent(e)).sort(byName)
    : []
  const pickerCount = availList.length + assignedList.length + outList.length + staleList.length

  const handleSave = () => {
    // Wrong-day guard. The copy-to-other-days flow funnels through this same onSave,
    // so this single confirm covers it too — do not add a second prompt downstream.
    if (isTodayCell || isPastCell) {
      const msg = isTodayCell
        ? `Save changes to TODAY (${dayLabel})? This changes today's live schedule.`
        : `Save changes to a PAST day (${dayLabel})?`
      if (!window.confirm(msg)) return
    }
    // Prune per-worker overrides to workers currently on the crew.
    const prunedCC = {}
    workers.forEach(w => { if (workerCostCodes[w]) prunedCC[w] = workerCostCodes[w] })
    onSave({ foreman, count, task, workers, default_cost_code: defaultCostCode.trim(), worker_cost_codes: prunedCC }, copyDays)
  }

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-title">{job.num} {job.name}</div>
        <div className="modal-sub">
          {fmtDow(d)} {fmtDate(d)}
          {outCount > 0 && <span style={{ color: 'var(--red-text)', marginLeft: 6 }}>· {outCount} out today</span>}
        </div>

        {(isTodayCell || isPastCell) && (
          <div style={{
            background: isTodayCell ? '#FEF3C7' : '#FEE2E2',
            color: isTodayCell ? '#92400E' : 'var(--red-text)',
            fontWeight: 700,
            fontSize: 13,
            padding: '8px 12px',
            borderRadius: 6,
            margin: '8px 0',
          }}>
            {isTodayCell
              ? `⚠ You are editing TODAY — ${dayLabel}. Did you mean tomorrow?`
              : `⚠ You are editing a PAST day — ${dayLabel}.`}
          </div>
        )}

        <div className="modal-row"><label>Foreman</label>
          <select className="form-select" value={foreman} onChange={e => {
              const newForeman = e.target.value
              const oldForeman = foreman
              setForeman(newForeman)
              // Remove old foreman from workers if they were auto-added
              setWorkers(w => {
                let updated = w.filter(name => name !== oldForeman)
                // Add new foreman as first worker if not already there
                if (newForeman && !updated.includes(newForeman)) {
                  updated = [newForeman, ...updated]
                  setCount(c => c + 1)
                }
                return updated
              })
            }} style={{ flex: 1 }}>
            <option value="">— Select foreman —</option>
            {[...roster]
              .filter(isSchedulable)
              .sort((a, b) => {
                if (a.isForeman && !b.isForeman) return -1
                if (!a.isForeman && b.isForeman) return 1
                return a.name.localeCompare(b.name)
              })
              .map(e => {
                const isOut = isOutOnDate(e, d)
                return (
                  <option key={e.id} value={e.name} disabled={isOut} style={{ color: isOut ? '#aaa' : undefined }}>
                    {e.isForeman ? '★ ' : ''}{e.name}{isOut ? ' (time off)' : ''}
                  </option>
                )
              })
            }
          </select>
        </div>
        <div className="modal-row"><label>Crew count</label>
          <div className="cnt-control">
            <button className="btn btn-sm" onClick={() => setCount(c => Math.max(0, c - 1))}>−</button>
            <span className="cnt-val">{count}</span>
            <button className="btn btn-sm" onClick={() => setCount(c => c + 1)}>+</button>
          </div>
        </div>
        <div className="modal-row" style={{ alignItems: 'flex-start' }}><label style={{ paddingTop: 4 }}>Task / notes</label>
          <textarea className="form-textarea" value={task} onChange={e => setTask(e.target.value)} style={{ flex: 1 }} />
        </div>
        <div className="modal-row"><label>Default cost code</label>
          <SearchCombo className="form-input" items={ccItems} value={defaultCostCode}
            onChange={setDefaultCostCode}
            placeholder={jobCostCodes.length ? 'Whole-crew default (optional)' : 'Whole-crew default — code'}
            wrapStyle={{ flex: 1 }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 10, marginTop: -2 }}>
          Pre-fills every crew member's cost code on the daily field form. Override per worker below if needed.
        </div>

        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
          Workers assigned ({workers.length})
        </div>
        <div className="worker-list-box">
          {workers.length ? workers.map((w, i) => {
            const emp = roster.find(e => e.name === w) || {}
            // Payroll-link flag (Phase 3): a real named worker who doesn't resolve
            // to an employees row has no payroll id. Union placeholders are exempt.
            const isPh = typeof w === 'string' && w.startsWith('[') && w.endsWith(']')
            const noId = !isPh && emp.id == null
            const dblJobs = !isPh ? assignedElsewhere[w] : null
            return (
              <div key={i} className="worker-list-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: dblJobs ? 'var(--amber-text)' : undefined }}>{w}</span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 5 }}>{emp.local}</span>
                  {dblJobs && (
                    <span title={`Also assigned to ${dblJobs.join(', ')} this day`}
                      style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--amber-text)',
                        background: 'var(--amber-bg)', border: '0.5px solid var(--amber-text)',
                        borderRadius: 4, padding: '0 5px', whiteSpace: 'nowrap' }}>
                      ⚠ double booked · {dblJobs.join(', ')}
                    </span>
                  )}
                  {!isPh && (noId
                    ? <span title="No matching payroll employee — add them in Foundation, then re-pick from the list below." style={{ marginLeft: 6, fontSize: 10, color: '#b45309', background: '#fff8f0', border: '0.5px solid #f0d6b0', borderRadius: 4, padding: '0 5px' }}>⚠ no ID</span>
                    : <span title="Payroll employee number (Foundation / P4C)" style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }}>#{emp.id}</span>)}
                </div>
                <SearchCombo
                  items={ccItems}
                  minListWidth={260}
                  value={workerCostCodes[w] || ''}
                  onChange={v => setWorkerCC(w, v)}
                  placeholder={defaultCostCode.trim() ? `${defaultCostCode.trim()} (default)` : 'cost code'}
                  title="Per-worker cost code override"
                  inputStyle={{ width: 110, padding: '4px 6px', fontSize: 11, border: '0.5px solid var(--border2)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', marginRight: 6 }}
                />
                <button className="remove-btn" onClick={() => removeWorker(i)}>×</button>
              </div>
            )
          }) : <div style={{ padding: 10, fontSize: 11, color: 'var(--text3)' }}>None assigned yet</div>}
        </div>

        {/* Add worker: dropdown of who's available today + search */}
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>Add worker</div>
        <div ref={pickerRef} style={{ position: 'relative', marginBottom: 10 }}>
          <div style={{ position: 'relative' }}>
            <input
              className="form-input"
              value={search}
              onFocus={() => setPickerOpen(true)}
              onChange={e => { setSearch(e.target.value); setPickerOpen(true) }}
              placeholder="Click to see who's available, or type to search..."
              style={{ width: '100%', paddingRight: 28, boxSizing: 'border-box' }}
            />
            <span onClick={() => setPickerOpen(o => !o)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                cursor: 'pointer', color: 'var(--text3)', fontSize: 10, userSelect: 'none' }}>
              {pickerOpen ? '▲' : '▼'}
            </span>
          </div>
          {pickerOpen && pickerCount > 0 && (
            <div style={{
              border: '0.5px solid var(--border2)', borderTop: 'none', borderRadius: '0 0 8px 8px',
              background: 'var(--surface)', maxHeight: 240, overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              {availList.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 9, fontWeight: 700, color: 'var(--text3)',
                  textTransform: 'uppercase', letterSpacing: '.4px', background: 'var(--surface2)' }}>
                  Available ({availList.length})
                </div>
              )}
              {availList.map(emp => (
                <div key={emp.id} onClick={() => addWorker(emp)}
                  style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{emp.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{emp.local ? `${emp.local} · ` : ''}#{emp.id}{emp.city ? ` · ${emp.city}` : ''}</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 500 }}>+ Add</span>
                </div>
              ))}
              {assignedList.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 9, fontWeight: 700, color: 'var(--amber-text)',
                  textTransform: 'uppercase', letterSpacing: '.4px', background: 'var(--amber-bg)' }}>
                  Already assigned today ({assignedList.length})
                </div>
              )}
              {assignedList.map(emp => (
                <div key={emp.id} onClick={() => addWorker(emp)}
                  title={`Already on ${assignedElsewhere[emp.name].join(', ')} — click to add here too`}
                  style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--amber-bg)' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = 0.8 }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = 1 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--amber-text)' }}>{emp.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--amber-text)', marginLeft: 6, fontWeight: 500 }}>
                      on {assignedElsewhere[emp.name].join(', ')}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{emp.local ? `${emp.local} · ` : ''}#{emp.id}{emp.city ? ` · ${emp.city}` : ''}</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--amber-text)', fontWeight: 500, whiteSpace: 'nowrap' }}>+ Add</span>
                </div>
              ))}
              {outList.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 9, fontWeight: 700, color: 'var(--text3)',
                  textTransform: 'uppercase', letterSpacing: '.4px', background: 'var(--surface2)' }}>
                  Time off ({outList.length})
                </div>
              )}
              {outList.map(emp => (
                <div key={emp.id}
                  style={{ padding: '7px 12px', cursor: 'not-allowed', borderBottom: '0.5px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    opacity: 0.5, background: 'var(--surface2)' }}
                >
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{emp.name}</span>
                    <span style={{ fontSize: 10, color: '#A32D2D', marginLeft: 6, fontWeight: 500 }}>· time off</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{emp.local ? `${emp.local} · ` : ''}#{emp.id}{emp.city ? ` · ${emp.city}` : ''}</span>
                  </div>
                </div>
              ))}
              {staleList.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: 9, fontWeight: 700, color: 'var(--red-text)',
                  textTransform: 'uppercase', letterSpacing: '.4px', background: 'var(--red-bg)' }}>
                  Not on recent crews ({staleList.length})
                </div>
              )}
              {staleList.map(emp => (
                <div key={emp.id} onClick={() => addWorker(emp)}
                  title={`Hasn't worked in the last ${RECENT_DAYS} days — click to add anyway`}
                  style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--red-bg)', opacity: 0.75 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--red-text)' }}>{emp.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--red-text)', marginLeft: 6 }}>
                      {emp.dateLastWorked ? `last worked ${fmtLastWorked(emp.dateLastWorked)}` : 'no recent work date'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{emp.local ? `${emp.local} · ` : ''}#{emp.id}{emp.city ? ` · ${emp.city}` : ''}</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--red-text)', fontWeight: 500, whiteSpace: 'nowrap' }}>+ Add</span>
                </div>
              ))}
            </div>
          )}
          {dupWarn && (
            <div style={{ fontSize: 11, color: 'var(--amber-text)', background: 'var(--amber-bg)', border: '0.5px solid var(--amber-text)', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
              ⚠ {dupWarn}
            </div>
          )}
          {pickerOpen && pickerCount === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', padding: '6px 2px' }}>
              {q ? `No workers match "${search}"` : 'No one else on the current crew is free — type a name to search the full employee list'}
            </div>
          )}
        </div>

        {/* Union placeholder workers */}
        <div style={{ marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            defaultValue=""
            onChange={e => {
              if (!e.target.value) return
              const local = e.target.value
              // Generate unique placeholder name e.g. [Local 4 #2] if already exists
              const existing = workers.filter(w => w.startsWith(`[${local}`)).length
              const name = existing > 0 ? `[${local} #${existing + 1}]` : `[${local}]`
              setWorkers(w => [...w, name])
              setCount(c => c + 1)
              e.target.value = ''
            }}
            style={{ flex: 1, borderRadius: 8, border: '0.5px solid var(--border2)', padding: '7px 10px', fontSize: 12, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }}
          >
            <option value="">Add union placeholder...</option>
            {[...new Set(roster.filter(e => e.active !== false && e.local).map(e => e.local))].sort().map(local => (
              <option key={local} value={local}>{local}</option>
            ))}
          </select>
        </div>

        {/* Copy to other days */}
        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCopy ? 8 : 0 }}>
            <button className="btn btn-sm" onClick={() => { setShowCopy(s => !s); setCopyDays([]); setCopyDone(false) }}
              style={{ fontSize: 11 }}>
              {showCopy ? '✕ Cancel copy' : '⧉ Copy this day to other days'}
            </button>
            {showCopy && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" style={{ fontSize: 10 }} onClick={selectAll}>Select all</button>
                <button className="btn btn-sm" style={{ fontSize: 10 }} onClick={clearAll}>Clear</button>
              </div>
            )}
          </div>
          {showCopy && !copyDone && (
            <>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
                Pick the days to copy this crew assignment to:
              </div>
              {/* Day-letter header */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                {DAY_LETTERS.map((ltr, i) => (
                  <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text3)' }}>{ltr}</div>
                ))}
              </div>
              {/* Calendar weeks */}
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {calendarWeeks.map((week, wi) => (
                  <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                    {week.map((cd, di) => {
                      const k = dk(cd)
                      if (!workDaySet.has(k)) return <div key={di} />
                      const isCurrent = k === dateKey
                      const sel = copyDays.includes(k)
                      const warn = sel && cd < TODAY
                      let bg = 'transparent', fg = 'var(--text2)', bc = 'var(--border2)'
                      if (isCurrent) { bg = 'var(--surface2)'; fg = 'var(--text3)'; bc = 'var(--border)' }
                      else if (warn) { bg = 'var(--amber-bg)'; fg = 'var(--amber-text)'; bc = 'var(--amber-text)' }
                      else if (sel) { bg = 'var(--blue)'; fg = '#fff'; bc = 'var(--blue)' }
                      return (
                        <button key={di} onClick={() => toggleCopyDay(k)} disabled={isCurrent}
                          title={isCurrent ? 'This day' : `${fmtDow(cd)} ${fmtDate(cd)}`}
                          style={{
                            aspectRatio: '1 / 1',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${bc}`, borderRadius: 6,
                            background: bg, color: fg,
                            cursor: isCurrent ? 'default' : 'pointer',
                            opacity: isCurrent ? 0.6 : 1,
                            fontSize: 12, fontWeight: 500, padding: 0, lineHeight: 1,
                          }}>
                          <span>{cd.getDate()}</span>
                          {warn && <span style={{ fontSize: 9 }}>⚠️</span>}
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
              {hasPastSelected && (
                <div style={{ fontSize: 11, color: 'var(--amber-text)', marginTop: 6 }}>
                  ⚠ One or more selected dates are in the past
                </div>
              )}
              {copyDays.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--green-text)', background: 'var(--green-bg)', borderRadius: 6, padding: '5px 10px', marginTop: 8, marginBottom: 8 }}>
                  Will copy to {copyDays.length} day{copyDays.length !== 1 ? 's' : ''} when you save
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={handleSave}>
            {copyDays.length > 0 ? `Save + copy to ${copyDays.length} day${copyDays.length !== 1 ? 's' : ''}` : 'Save'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Time Off Modal ────────────────────────────────────────────────────────────
export function TimeOffModal({ emp, roster, onAdd, onRemove, onClose }) {
  const [selId, setSelId] = useState(emp?.id ?? roster[0]?.id)
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState('')
  const selEmp = roster.find(e => e.id === parseInt(selId)) || emp

  const addTimeOff = () => {
    if (!from || !to || !selEmp) return
    onAdd(selEmp.id, from, to, reason)
    setReason('')
  }
  const removeTimeOff = (idx) => {
    onRemove(selEmp.id, idx)
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">Manage time off</div>
        <div className="modal-sub">Mark dates a worker is unavailable</div>
        <div className="modal-row"><label>Worker</label>
          <select className="form-select" value={selId} onChange={e => setSelId(e.target.value)} style={{ flex: 1 }}>
            {roster.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="modal-row"><label>From</label>
          <input className="form-input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ flex: 1 }} />
        </div>
        <div className="modal-row"><label>To</label>
          <input className="form-input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ flex: 1 }} />
        </div>
        <div className="modal-row"><label>Reason</label>
          <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="vacation, sick, personal..." style={{ flex: 1 }} />
        </div>
        <button className="btn btn-primary btn-full" style={{ marginBottom: 12 }} onClick={addTimeOff}>+ Add time off period</button>

        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>
          Recorded time off{selEmp ? ' — ' + selEmp.name : ''}
        </div>
        <div className="timeoff-list-box">
          {selEmp?.timeOff.length ? selEmp.timeOff.map((t, i) => (
            <div key={i} className="timeoff-row">
              <span>{t.from} – {t.to}{t.reason ? ' · ' + t.reason : ''}</span>
              <button className="remove-btn" onClick={() => removeTimeOff(i)}>×</button>
            </div>
          )) : <div style={{ padding: 10, fontSize: 11, color: 'var(--text3)' }}>No time off recorded</div>}
        </div>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  )
}
